import React, { useState, Fragment } from 'react';
import { EditIcon, TrashIcon, EyeIcon, XCircleIcon, AlertTriangleIcon, CheckCircleIcon, InfoIcon, BellIcon, SettingsIcon, RefreshCwIcon, ClipboardIcon } from 'lucide-react';
import { Message } from './MessagesManagement';
import { StatusBadge } from '../../StatusBadge';
interface MessagesListProps {
  messages: Message[];
  canEditMessage: (message: Message) => boolean;
  onEdit: (message: Message) => void;
  onDelete: (id: string) => void;
  onResetOverride: (id: string) => void;
  locale: string;
}
export function MessagesList({
  messages,
  canEditMessage,
  onEdit,
  onDelete,
  onResetOverride,
  locale
}: MessagesListProps) {
  const [expandedMessage, setExpandedMessage] = useState<string | null>(null);
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getTypeIcon = (type: Message['type']) => {
    switch (type) {
      case 'Error':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-18611" />;
      case 'Warning':
        return <AlertTriangleIcon className="h-5 w-5 text-amber-500" data-id="element-18612" />;
      case 'Success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-18613" />;
      case 'Info':
        return <InfoIcon className="h-5 w-5 text-blue-500" data-id="element-18614" />;
      case 'Notification':
        return <BellIcon className="h-5 w-5 text-purple-500" data-id="element-18615" />;
      case 'System':
        return <SettingsIcon className="h-5 w-5 text-gray-500" data-id="element-18616" />;
      default:
        return null;
    }
  };
  const getTypeColor = (type: Message['type']) => {
    switch (type) {
      case 'Error':
        return 'text-red-700 bg-red-50';
      case 'Warning':
        return 'text-amber-700 bg-amber-50';
      case 'Success':
        return 'text-green-700 bg-green-50';
      case 'Info':
        return 'text-blue-700 bg-blue-50';
      case 'Notification':
        return 'text-purple-700 bg-purple-50';
      case 'System':
        return 'text-gray-700 bg-gray-50';
      default:
        return 'text-gray-700 bg-gray-50';
    }
  };
  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key).then(() => {
      // Could show a toast notification here
      console.log(`Copied ${key} to clipboard`);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };
  const toggleExpand = (id: string) => {
    if (expandedMessage === id) {
      setExpandedMessage(null);
    } else {
      setExpandedMessage(id);
    }
  };
  if (messages.length === 0) {
    return <div className="text-center py-12" data-id="element-18617">
        <InfoIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-18618" />
        <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-18619">
          No messages found
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-18620">
          No messages match your current filters. Try adjusting your search
          criteria.
        </p>
      </div>;
  }
  return <div className="overflow-x-auto" data-id="element-18621">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-18622">
        <thead data-id="element-18623">
          <tr data-id="element-18624">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18625">
              Type
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18626">
              Key & Message
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18627">
              Module
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18628">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18629">
              Usage
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18630">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-18631">
          {messages.map(message => <Fragment key={message.id} data-id="element-18632">
              <tr className={expandedMessage === message.id ? 'bg-gray-50' : 'hover:bg-gray-50'} data-id="element-18633">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-18634">
                  <div className="flex items-center" data-id="element-18635">
                    <span className="flex-shrink-0" data-id="element-18636">
                      {getTypeIcon(message.type)}
                    </span>
                    <span className="ml-2 text-sm font-medium text-gray-900" data-id="element-18637">
                      {message.type}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-18638">
                  <div data-id="element-18639">
                    <div className="flex items-center text-sm font-medium text-gray-900" data-id="element-18640">
                      <button onClick={() => handleCopyKey(message.key)} className="text-gray-500 hover:text-gray-700 mr-1" title="Copy key" data-id="element-18641">
                        <ClipboardIcon className="h-4 w-4" data-id="element-18642" />
                      </button>
                      <span className="font-mono" data-id="element-18643">{message.key}</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500 line-clamp-2" data-id="element-18644">
                      {message.overriddenMessage ? <span className="text-indigo-600 font-medium" data-id="element-18645">
                          {message.overriddenMessage}
                        </span> : message.defaultMessage}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-18646">
                  <div className="text-sm text-gray-900" data-id="element-18647">{message.module}</div>
                  {message.context && <div className="text-sm text-gray-500" data-id="element-18648">
                      {message.context}
                    </div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-18649">
                  <div className="flex flex-col space-y-1" data-id="element-18650">
                    <StatusBadge status={message.isActive ? 'success' : 'error'} text={message.isActive ? 'Active' : 'Inactive'} data-id="element-18651" />
                    {message.isSystemLocked && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-18652">
                        System Locked
                      </span>}
                    {message.overriddenMessage && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-18653">
                        Overridden
                      </span>}
                    {message.editableByRoles.length > 0 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-18654">
                        RBAC Limited
                      </span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-18655">
                  <div data-id="element-18656">Count: {message.usageCount}</div>
                  <div data-id="element-18657">Last: {formatDate(message.lastUsedAt)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-18658">
                  <div className="flex justify-end space-x-2" data-id="element-18659">
                    <button onClick={() => toggleExpand(message.id)} className="text-indigo-600 hover:text-indigo-900" title="View details" data-id="element-18660">
                      <EyeIcon className="h-5 w-5" data-id="element-18661" />
                    </button>
                    {canEditMessage(message) && <>
                        <button onClick={() => onEdit(message)} className="text-indigo-600 hover:text-indigo-900" title="Edit message" data-id="element-18662">
                          <EditIcon className="h-5 w-5" data-id="element-18663" />
                        </button>
                        {message.overriddenMessage && <button onClick={() => onResetOverride(message.id)} className="text-amber-600 hover:text-amber-900" title="Reset override" data-id="element-18664">
                            <RefreshCwIcon className="h-5 w-5" data-id="element-18665" />
                          </button>}
                        {!message.isSystemLocked && <button onClick={() => onDelete(message.id)} className="text-red-600 hover:text-red-900" title="Delete message" data-id="element-18666">
                            <TrashIcon className="h-5 w-5" data-id="element-18667" />
                          </button>}
                      </>}
                  </div>
                </td>
              </tr>
              {expandedMessage === message.id && <tr className="bg-gray-50" data-id="element-18668">
                  <td colSpan={6} className="px-6 py-4" data-id="element-18669">
                    <div className="space-y-4" data-id="element-18670">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-18671">
                        <div data-id="element-18672">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18673">
                            Default Message
                          </h4>
                          <div className={`p-3 rounded-md text-sm ${getTypeColor(message.type)}`} data-id="element-18674">
                            {message.defaultMessage}
                          </div>
                        </div>
                        {message.overriddenMessage && <div data-id="element-18675">
                            <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18676">
                              Overridden Message
                            </h4>
                            <div className={`p-3 rounded-md text-sm ${getTypeColor(message.type)}`} data-id="element-18677">
                              {message.overriddenMessage}
                            </div>
                          </div>}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-18678">
                        <div data-id="element-18679">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18680">
                            Created
                          </h4>
                          <div className="text-sm text-gray-500" data-id="element-18681">
                            <div data-id="element-18682">{formatDate(message.createdAt)}</div>
                            <div data-id="element-18683">By: {message.createdBy}</div>
                          </div>
                        </div>
                        {message.modifiedAt && <div data-id="element-18684">
                            <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18685">
                              Last Modified
                            </h4>
                            <div className="text-sm text-gray-500" data-id="element-18686">
                              <div data-id="element-18687">{formatDate(message.modifiedAt)}</div>
                              <div data-id="element-18688">By: {message.modifiedBy}</div>
                            </div>
                          </div>}
                        <div data-id="element-18689">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18690">
                            RBAC Permissions
                          </h4>
                          {message.editableByRoles.length > 0 ? <div className="flex flex-wrap gap-1" data-id="element-18691">
                              {message.editableByRoles.map(role => <span key={role} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-18692">
                                  {role}
                                </span>)}
                            </div> : <div className="text-sm text-gray-500" data-id="element-18693">
                              {message.isSystemLocked ? 'System locked - no editing allowed' : 'No RBAC restrictions'}
                            </div>}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>}
            </Fragment>)}
        </tbody>
      </table>
    </div>;
}